import { Box, Button, ButtonBase, Checkbox, Menu, MenuItem, OutlinedInput, InputAdornment, ListItemIcon, ListItemText, styled, Typography, useTheme } from "@mui/material";
import Stack from '@mui/material/Stack';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import { bgBlur } from 'src/theme/css';
import { useOffSetTop } from 'src/hooks/use-off-set-top';
import { useResponsive } from 'src/hooks/use-responsive';
import Logo from 'src/components/logo';
import SvgColor from 'src/components/svg-color';
import { useSettingsContext } from 'src/components/settings';
import { useDateRangeSelect } from 'src/components/generic-select/date-range-select';
import { useEffect, useMemo, useRef, useState } from 'react';
import Iconify from 'src/components/iconify';
import DateRangeSelect from 'src/components/generic-select/date-range-select';
import { fDate, getDateRangeIso } from 'src/utils/format-time';
import { useGetCountriesBrandWaybackQuery, useGetCountriesQuery, useGetDevicesBrandWaybackQuery, useGetDevicesQuery, useGetKeywordsBrandWaybackQuery, useGetKeywordsQuery } from 'src/context/api/trafficAnalysis/api';
import { useDispatch, useSelector } from "react-redux";
import InfiniteScrollKeywords from "src/sections/overview/dashboards/views/InfiniteScrollKeywords";
import Autocomplete from '@mui/material/Autocomplete';
import Chip from '@mui/material/Chip';
import TextField from '@mui/material/TextField';
import { FlagIcon } from "src/components/iconify/flagIcon";
import { subDays, differenceInDays } from "date-fns";
import { useCurrentWorkspace } from "src/context/reducers/app-settings";
import { useLocales } from "src/locales";
import InfiniteScrollKeywordsBrandWayback from "src/sections/overview/dashboards/views/InfiniteScrollKeywordBrandWayback";
import { useCurrentUserQuery } from "src/context/api/auth";
import { RootState } from "src/context/store";
import { useSearchParams } from "react-router-dom";
import { useRouter, usePathname } from "src/routes/hooks";
import { NavbarProps, AdType } from "src/components/competitive-intelligence/types";
import { setAdType as setAdTypeAction, setSearchQuery as setSearchQueryAction, setSortBy as setSortByAction, setSortDirection as setSortDirectionAction } from 'src/context/reducers/competitive-intelligence/adsSlice';
import mixpanel from "mixpanel-browser";
import { setFilterDate, setFilterDevice, setFilterCountry, setFilterKeyword, setFilterTimestep, setFilterDateCompare, setFilterCompare, setFilterPlatform, setFilterCampaignType, setFilterMinDatePicker, setExtraOptions, setSavingsFormulaFilter, setFilterConversionType, setFilterLoadedKeyword } from '../../context/reducers/filter-settings/filterSlice';
import { AccountPopover, NotificationsPopover } from '../_common';
import { HEADER, NAV } from '../config-layout';
import countriesJson from "../../utils/countries.json"
import FiltersButton from "../_common/filters-button";
import SettingsPanel from "../_common/settings/settings-panel";

type Props = {
  onOpenNav?: VoidFunction;
};

// Moved out of Header to comply with react/no-unstable-nested-components
const TabButton = styled(ButtonBase)(({ theme }: any) => ({
  padding: theme.spacing(0.5, 1.5),
  borderRadius: 6,
  fontSize: 15,
  fontWeight: 400,
  color: theme.palette.text.secondary,
  "&.active": {
    fontWeight: 600,
    color: theme.palette.mode === "dark" ? theme.palette.common.white : 'black',
    backgroundColor: theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.08)" : "rgba(145, 158, 171, 0.2)",
  },
  "&:hover": {
    color: theme.palette.mode === "dark" ? theme.palette.common.white : 'black',
    backgroundColor: theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.05)" : "rgba(145, 158, 171, 0.1)",
  },
}));

// Moved out of Header to comply with react/no-unstable-nested-components
const KoBiddingDropdown = ({ router, location }: any) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const options = [
    { label: "Max CPC", path: "/optimization/activations/ko-bidding/max-cpc" },
    { label: "Target ROAS / Maximize Conversion Value", path: "/optimization/activations/ko-bidding/troas" },
    { label: "Target CPA / Maximize Conversion", path: "/optimization/activations/ko-bidding/tcpa" },
    { label: "Target Impression Share", path: "/optimization/activations/ko-bidding/target-impression-share" },
    { label: "Shopping", path: "/admin/features/live-bidding/ad-group-based" },
  ];

  const current = options.find((opt) => location.pathname === opt.path) || options[0];

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (path?: string) => {
    setAnchorEl(null);
    if (path) router.push(path);
  };

  return (
    <>
      <TabButton
        onClick={handleClick}
        className={
          location.pathname.startsWith("/optimization/activations/ko-bidding/")
            ? "active"
            : ""
        }
        sx={{ mr: 1, display: "flex", alignItems: "center", gap: 0.5 }}
      >
        KO Bidding Manual: {current.label}
        <Iconify
          icon={open ? "eva:arrow-ios-upward-fill" : "eva:arrow-ios-downward-fill"}
          width={18}
          height={18}
        />
      </TabButton>

      <Menu anchorEl={anchorEl} open={open} onClose={() => handleClose()}>
        {options.map((opt) => (
          <MenuItem
            key={opt.path}
            onClick={() => handleClose(opt.path)}
            selected={location.pathname === opt.path}
          >
            {opt.label}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

// Competitive Intelligence Ads Header - moved out of Header to comply with lint rule
type CIAHeaderProps = Partial<NavbarProps>;

const CompetitiveIntelligenceAdsHeader = ({
  adType,
  counts = { ALL: 0, PLA: 0, TEXT: 0, RightPanel: 0 },
  searchQuery,
  sortBy,
  sortDirection,
}: CIAHeaderProps) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const ui = useSelector((state: any) => state.competitiveIntelligenceAdsUi || { adType: 'ALL', searchQuery: '', sortBy: 'Freq.', sortDirection: 'desc' });
  const effectiveAdType = adType ?? ui.adType;
  const effectiveSearchQuery = searchQuery ?? ui.searchQuery;
  const effectiveSortBy = sortBy ?? ui.sortBy;
  const effectiveSortDirection = sortDirection ?? ui.sortDirection;
  const [typeAnchorEl, setTypeAnchorEl] = useState<null | HTMLElement>(null);
  const typeOpen = Boolean(typeAnchorEl);
  const openTypeMenu = (event: any) => setTypeAnchorEl(event.currentTarget);
  const closeTypeMenu = () => setTypeAnchorEl(null);

  // Dropdown for sorting
  const [sortAnchorEl, setSortAnchorEl] = useState<null | HTMLElement>(null);
  const sortOpen = Boolean(sortAnchorEl);
  const openSortMenu = (event: any) => setSortAnchorEl(event.currentTarget);
  const closeSortMenu = () => setSortAnchorEl(null);

  const items = useMemo(() => ([
    { key: 'ALL' as AdType, label: 'All ads' },
    { key: 'PLA' as AdType, label: 'PLA' },
    { key: 'TEXT' as AdType, label: 'Text ads' },
  ]), [counts.ALL, counts.PLA, counts.TEXT]);

  const selected = items.find((i) => i.key === effectiveAdType);

  return (
    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ width: '100%' }}>
      {/* Left side: Ads type + filter icon */}
      <Stack direction="row" alignItems="center" spacing={1}>
        <Button
          size="small"
          variant="outlined"
          color="inherit"
          onClick={openTypeMenu}
          endIcon={<Iconify icon="mdi:chevron-down" fontSize="small" />}
          sx={{
            borderColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.16)' : 'rgba(0,0,0,0.23)',
            color: theme.palette.mode === 'dark' ? '#fff' : theme.palette.text.primary,
            textTransform: 'none',
            minWidth: 95,
            '&:hover': {
              borderColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.4)',
            }
          }}
        >
          {selected?.label || 'All ads'}
        </Button>
        <IconButton
          onClick={() => {
            // Delegate to the active page to open its own filters drawer
            window.dispatchEvent(new CustomEvent('openPageFiltersDrawer'))
          }}
          sx={{
            width: 32,
            height: 32,
            borderRadius: 1,
            border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.16)' : 'rgba(0,0,0,0.23)'}`,
            bgcolor: theme.palette.mode === 'dark' ? '#383F49' : '#F5F5F5',
            color: theme.palette.mode === 'dark' ? '#919EAB' : theme.palette.text.secondary,
            p: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            '&:hover': {
              bgcolor: theme.palette.mode === 'dark' ? '#3F4752' : '#E0E0E0',
              borderColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.4)',
            },
          }}
        >
          <Iconify icon="majesticons:filter-line" width={16} height={16} />
        </IconButton>
      </Stack>

      {/* Right side: Search + Sort by */}
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ minWidth: 0 }}>
        <OutlinedInput
          size="small"
          placeholder="Search"
          value={effectiveSearchQuery}
          onChange={(e) => dispatch(setSearchQueryAction(e.target.value))}
          startAdornment={<InputAdornment position="start"><Iconify icon="mdi:magnify" fontSize="small" /></InputAdornment>}
          sx={{
            width: '200px',
            bgcolor: 'transparent',
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.16)' : 'rgba(0,0,0,0.23)',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.4)',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.4)',
            },
            '& input': {
              color: theme.palette.text.primary,
            },
            '& input::placeholder': {
              color: theme.palette.text.secondary,
            }
          }}
        />
        <Box
          sx={{
            position: 'relative',
            border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.16)' : 'rgba(0,0,0,0.23)'}`,
            borderRadius: 1,
            width: 130,
            px: 1,
            pt: 1.5,
            pb: 1,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Button
            size="small"
            variant="text"
            color="inherit"
            onClick={openSortMenu}
            endIcon={<Iconify icon="mdi:chevron-down" fontSize="small" />}
            // c'est pas logique mais j'ai inversé la logique de la flèche par rapport au sort direction pour gagner du temps, c'est fonctionnel comme ça pour le moment
            startIcon={<Iconify icon={effectiveSortDirection === 'desc' ? 'gravity-ui:bars-descending-align-left' : 'gravity-ui:bars-ascending-align-left'} fontSize="small" />}
            sx={{
              color: theme.palette.mode === 'dark' ? '#fff' : theme.palette.text.primary,
              textTransform: 'none',
              borderRadius: 1,
              px: 0.5,
              height: 14,
              minWidth: 10,
              justifyContent: 'space-between',
              '& .MuiButton-startIcon': { mr: 1 },
              '& .MuiButton-endIcon': { ml: 1 },
              fontWeight: 600,
            }}
          >
            {effectiveSortBy}
          </Button>
        </Box>
      </Stack>

      {/* Type menu */}
      <Menu
        anchorEl={typeAnchorEl}
        open={typeOpen}
        onClose={closeTypeMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        MenuListProps={{ sx: { textAlign: 'right', minWidth: 90, maxWidth: 90 } }}
        slotProps={{ paper: { sx: { mt: 1 } } }}
      >
        {items.map((item) => (
          <MenuItem
            key={item.key}
            selected={item.key === effectiveAdType}
            onClick={() => {
              dispatch(setAdTypeAction(item.key));
              closeTypeMenu();
            }}
          >
            <ListItemText primary={item.label} />
          </MenuItem>
        ))}
      </Menu>

      {/* Sort menu */}
      <Menu
        anchorEl={sortAnchorEl}
        open={sortOpen}
        onClose={closeSortMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        slotProps={{ paper: { sx: { mt: 2, minWidth: 135 } } }}
      >
        {['Freq.', 'Position', 'Price'].map((opt) => (
          <MenuItem
            key={opt}
            selected={opt === effectiveSortBy}
            onClick={() => {
              const newSort = opt as any;
              if (newSort === effectiveSortBy) {
                dispatch(setSortDirectionAction(effectiveSortDirection === 'asc' ? 'desc' : 'asc'));
              } else {
                dispatch(setSortByAction(newSort));
                dispatch(setSortDirectionAction(newSort === 'Position' ? 'asc' : 'desc'));
              }
              closeSortMenu();
            }}
          >
            <ListItemText primary={opt} />
          </MenuItem>
        ))}
      </Menu>
    </Stack>
  );
};

export default function Header({ onOpenNav }: Props) {
  const theme = useTheme();
  const { t } = useLocales();
  const [filterDevice, setFilterDeviceState] = useState<any>(undefined);
  const [filterCountry, setFilterCountryState] = useState<any>(undefined);
  const [filterKeyword, setFilterKeywordState] = useState<any>(undefined);
  const [filterPlatform, setFilterPlatformState] = useState<any>(undefined);
  const [CampaignType, setCampaignType] = useState<any>(undefined)
  const [resetKeywordFilter, setResetKeywordFilter] = useState<any>(false)
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState<boolean>(false);

  const filterStore = useSelector((state: any) => state.filters);
  const [showFilters, setShowFilters] = useState<boolean>(true);

  const regexCompetitor = /^\/competitor\/([^/]+)\/$/;
  const regexCompetirorsMonitoring = /^\/competitors-monitoring$/;
  const regexCompetirorsFocus = /^\/competitor-focus$/;
  const regexPLAGlobalFocus = /^\/product-listing-ads$/;
  const regexTXTGlobalFocus = /^\/text-ads$/;
  const regexBusinessOverview = /^\/business-overview$/;

  const matchCompetitor = location.pathname.match(regexCompetitor);
  const matchCompetitorMonitoring = location.pathname.match(regexCompetirorsMonitoring);
  const matchCompetitorFocus = location.pathname.match(regexCompetirorsFocus);
  const matchPLAGlobalFocus = location.pathname.match(regexPLAGlobalFocus);
  const matchTXTGlobalFocus = location.pathname.match(regexTXTGlobalFocus);
  const matchBusinessOverview = location.pathname.match(regexBusinessOverview);

  const workspace: any = useCurrentWorkspace();
  const optionRefs: any = useRef({}); // Référence pour suivre les options survolées

  const [compareStartDate, setCompareStartDate] = useState<any>(filterStore?.compareStart);
  const [compareEndDate, setCompareEndDate] = useState<any>(filterStore?.compareEnd);
  const [searchParams,] = useSearchParams();

  const { data: user }: any = useCurrentUserQuery();

  const settings = useSettingsContext();

  const extraOptions = useSelector((state: RootState) => state.filters.extraOptions);
  const router = useRouter()
  const pathname = usePathname();

  const dispatch = useDispatch()

  useEffect(() => {
    if (searchParams.has('mode') && extraOptions.mode !== searchParams.get('mode')) {
      dispatch(setExtraOptions({ ...extraOptions, mode: searchParams.get('mode') as "base" | "activations" }));
    }
  }, [searchParams, dispatch, extraOptions]);

  // Close header SettingsPanel when route changes to avoid leaking drawer state between pages
  useEffect(() => {
    if (isFilterDrawerOpen) setIsFilterDrawerOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const isNavHorizontal = settings.themeLayout === 'horizontal';
  const isNavMini = settings.themeLayout === 'mini';
  const lgUp = useResponsive('up', 'lg');
  const offsetTop = false;
  const [minWidth, setMinWidth] = useState(200); // Valeur par défaut

  const [start, end] = useMemo(() => {
    const startDate = new Date();
    startDate.setMonth(startDate.getUTCMonth() - 1);
    startDate.setDate(startDate.getUTCDate());
    const endDate = new Date();
    endDate.setDate(endDate.getUTCDate() - 1);
    return [startDate, endDate];
  }, []);

  const dateRangeSelect = useDateRangeSelect({
    preset: "last_30d",
    step: "year_week",
    selected: "custom",
    start,
    end
  });

  useEffect(() => {
    dispatch(setFilterDateCompare({
      compareStart: fDate(compareStartDate, "yyyy-MM-dd"),
      compareEnd: fDate(compareEndDate, "yyyy-MM-dd")
    }))
  }, [compareStartDate, compareEndDate, dispatch])

  const updateFilterDate = (startDate: Date | null, endDate: Date | null) => {
    if (startDate && endDate) {
      const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const { start, end } = getDateRangeIso(startDate, endDate, userTimeZone);

      dispatch(setFilterDate({ start, end }));

      // Calculer et mettre à jour la granularité
      const daysDifference = differenceInDays(endDate, startDate);
      let newGranularity = 'date';
      if (daysDifference > 365) {
        newGranularity = 'year_month';
      } else if (daysDifference > 90) {
        newGranularity = 'year_week';
      }
      dispatch(setFilterTimestep(newGranularity));
    }
  };
  const skipQuery = !dateRangeSelect.start || !dateRangeSelect.end || !workspace?.id;

  const { data: devicesData } = useGetDevicesQuery({
    startDate: filterStore?.start !== undefined ? fDate(filterStore?.start, "yyyy-MM-dd") : null,
    endDate: filterStore?.end !== undefined ? fDate(filterStore?.end, "yyyy-MM-dd") : null,
    countries: filterStore.countries,
    keywords: filterStore.keywords,
    workspaceId: workspace?.id,
    platforms: filterStore?.platforms
  },
    {
      skip: skipQuery,
      refetchOnMountOrArgChange: false,
    }
  );

  const { data: devicesDataBrandWayback } = useGetDevicesBrandWaybackQuery({
    startDate: filterStore?.start !== undefined ? fDate(filterStore?.start, "yyyy-MM-dd") : null,
    endDate: filterStore?.end !== undefined ? fDate(filterStore?.end, "yyyy-MM-dd") : null,
    countries: filterStore.countries,
    keywords: filterStore.keywords,
    workspaceId: workspace?.id,
    platforms: filterStore?.platforms
  },
    {
      skip: (!filterStore?.start ||
        !filterStore?.end ||
        !workspace?.id ||
        location?.pathname === "/brand-overview" ||
        filterStore.keywords?.length === 0
      ),
      refetchOnMountOrArgChange: false,
    }
  );

  const { data: countriesData } = useGetCountriesQuery({
    startDate: filterStore?.start !== undefined ? fDate(filterStore?.start, "yyyy-MM-dd") : null,
    endDate: filterStore?.end !== undefined ? fDate(filterStore?.end, "yyyy-MM-dd") : null,
    device: filterStore.devices,
    keywords: filterStore.keywords,
    workspaceId: workspace?.id,
    platforms: filterStore?.platforms
  }, {
    skip: skipQuery,
    refetchOnMountOrArgChange: false,
  });

  const { data: countriesDataBrandWayback } = useGetCountriesBrandWaybackQuery({
    startDate: filterStore?.start !== undefined ? fDate(filterStore?.start, "yyyy-MM-dd") : null,
    endDate: filterStore?.end !== undefined ? fDate(filterStore?.end, "yyyy-MM-dd") : null,
    device: filterStore.devices,
    keywords: filterStore.keywords,
    workspaceId: workspace?.id,
    platforms: filterStore?.platforms
  }, {
    skip: (!filterStore?.start ||
      !filterStore?.end ||
      !workspace?.id ||
      location?.pathname === "/brand-overview"
    ),
    refetchOnMountOrArgChange: false,
  });

  useEffect(() => {
    if (countriesDataBrandWayback && location.pathname === "/brand-wayback") {
      if (filterStore?.countries?.length === 0) {
        dispatch(
          setFilterCountry([countriesDataBrandWayback?.filters?.countries[0]])
        );
        setFilterCountryState(countriesDataBrandWayback?.filters?.countries);
      }
    }
  }, [countriesDataBrandWayback])

  const { data: keywordsData } = useGetKeywordsQuery({
    startDate: filterStore?.start !== undefined ? fDate(filterStore?.start, "yyyy-MM-dd") : null,
    endDate: filterStore?.end !== undefined ? fDate(filterStore?.end, "yyyy-MM-dd") : null,
    device: filterStore?.devices,
    countries: filterStore?.countries,
    workspaceId: workspace?.id,
    platforms: filterStore?.platforms
  }, {
    skip: skipQuery,
    refetchOnMountOrArgChange: false,
  });

  useEffect(() => {
    if (keywordsData) {
      dispatch(setFilterLoadedKeyword(keywordsData?.filters?.keywords))
    }
  }, [keywordsData])

  const { devices, countries, keywords, campaignType } = useMemo(() => {
    return { devices: devicesData?.filters?.devices, countries: countriesData?.filters?.countries, keywords: keywordsData?.filters?.keywords, campaignType: ['Search', 'Shopping'] }
  }, [devicesData, countriesData, keywordsData])

  useEffect(() => {
    // Toujours utiliser le fuseau local de l'utilisateur
    const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (filterStore?.datesForcedByActivation) return;

    if (lgUp && !workspace?.name.includes('Demo')) {
      if (dateRangeSelect?.start && dateRangeSelect?.end) {
        // Range utilisateur standard
        const { start, end } = getDateRangeIso(dateRangeSelect.start, dateRangeSelect.end, userTimeZone);
        if (filterStore?.start !== filterStore?.end) {
          dispatch(setFilterDate({ start, end }));
        }
      }
    } else {
      if (lgUp) {
        // Si granularité "date" on force une plage d'octobre 2024, sinon une autre plage
        if (dateRangeSelect?.start && dateRangeSelect?.end && filterStore.timestep === "date") {
          const newStartDate = new Date(2024, 8, 1); // 1er sept 2024
          const newEndDate = new Date(2024, 10, 30); // 30 nov 2024

          const { start, end } = getDateRangeIso(newStartDate, newEndDate, userTimeZone);

          dispatch(setFilterDate({ start, end }));
        } else {
          const newStartDate = new Date(2024, 8, 1); // 1er septembre 2024
          const newEndDate = new Date(2024, 10, 30); // 30 novembre 2024
          const { start, end } = getDateRangeIso(newStartDate, newEndDate, userTimeZone);

          dispatch(setFilterDate({ start, end }));
        }
      }
    }
  }, [dateRangeSelect]);


  useEffect(() => {
    setShowFilters(false);
    const timer = setTimeout(() => setShowFilters(true), 1000);
    return () => clearTimeout(timer);
  }, [workspace]);

  useEffect(() => {
    if (location?.pathname !== "/brand-wayback") {
      if (countries && !matchCompetitor && !matchCompetitorMonitoring && !matchCompetitorFocus && !matchPLAGlobalFocus && !matchTXTGlobalFocus) {
        setFilterCountryState(countries);
      }
    }
  }, [countries, filterCountry, matchCompetitor, matchCompetitorMonitoring, matchCompetitorFocus])

  useEffect(() => {
    if (location?.pathname !== "/brand-wayback") {
      if (keywords && !matchCompetitor && !matchCompetitorMonitoring && !matchCompetitorFocus && !matchPLAGlobalFocus) {
        setFilterKeywordState(keywords);
      }
    }
  }, [location.pathname])

  useEffect(() => {
    if (location?.pathname !== "/brand-wayback") {
      if (filterPlatform && !matchCompetitor && !matchCompetitorMonitoring && !matchCompetitorFocus && !matchPLAGlobalFocus) {
        setFilterPlatformState(filterPlatform);
      }
    }
  }, [filterPlatform, matchCompetitor, matchCompetitorMonitoring, matchCompetitorFocus, matchPLAGlobalFocus])

  useEffect(() => {
    if (campaignType) {
      setCampaignType(campaignType)
    }
  }, [campaignType])

  useEffect(() => {
    if (devices && !matchCompetitor) {
      setFilterDeviceState(devices);
    }
  }, [devices, matchCompetitor, location?.pathname, devicesDataBrandWayback, filterStore.devices]);

  const handleDeviceFilterChange = (selectedOptions: any) => {
    let updatedOptions;

    if (Array.isArray(selectedOptions)) {
      updatedOptions = selectedOptions.filter(
        (option: string) => option !== t('dashboard.filters.all_devices')
      );
    } else {
      updatedOptions = selectedOptions !== t('dashboard.filters.all_devices')
        ? [selectedOptions]
        : [];
    }

    dispatch(setFilterDevice(updatedOptions));
  };

  const handleCountryFilterChange = (selectedOptions: any) => {
    let updatedOptions;
    if (Array.isArray(selectedOptions)) {
      updatedOptions = selectedOptions.filter(
        (option: string) => option !== t('dashboard.filters.all_countries')
      );
    } else {
      updatedOptions = selectedOptions !== t('dashboard.filters.all_countries')
        ? [selectedOptions]
        : [];
    }

    if (location?.pathname === "/brand-wayback") {
      dispatch(setFilterKeyword([]))
    }

    dispatch(setFilterCountry(updatedOptions));


  };

  const handleKeywordFilterChange = (value: any) => {
    if (filterStore?.keywords) {
      if (filterStore?.keywords.includes(value)) {
        dispatch(setFilterKeyword(filterStore?.keywords.filter((keyword: any) => keyword !== value)))
      } else {
        const updatedKeywords = [...filterStore.keywords, value];
        dispatch(setFilterKeyword(updatedKeywords))
      }
    }
  };

  const handleCampaignTypeFilterChange = (selectedOptions: any) => {
    const updatedOptions = selectedOptions.filter((option: string) => option !== t('dashboard.filters.all'));
    dispatch(setFilterCampaignType(updatedOptions));
  };

  useEffect(() => {
    const newStartDate = new Date();
    newStartDate.setMonth(newStartDate.getUTCMonth() - 1);
    newStartDate.setDate(newStartDate.getUTCDate() + 1);

    const newEndDate = new Date();
    newEndDate.setDate(newEndDate.getUTCDate() - 1);

    dateRangeSelect.onChangeStartDate(newStartDate);
    dateRangeSelect.onChangeEndDate(newEndDate);

    const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const { start, end } = getDateRangeIso(newStartDate, newEndDate, userTimeZone);

    dispatch(setFilterDate({ start, end }));

    dispatch(setFilterDateCompare({
      compareStart: subDays(new Date(), 60).toISOString(),
      compareEnd: subDays(new Date(), 31).toISOString(),
    }))
  }, [])


  const handleResetAllFilters = () => {
    const newStartDate = new Date();
    newStartDate.setMonth(newStartDate.getUTCMonth() - 1);
    newStartDate.setDate(newStartDate.getUTCDate() + 1);

    const newEndDate = new Date();
    newEndDate.setDate(newEndDate.getUTCDate() - 1);

    dateRangeSelect.onChangeStartDate(newStartDate);
    dateRangeSelect.onChangeEndDate(newEndDate);

    if (workspace?.name.includes('Demo')) {
      dispatch(setFilterTimestep('date'));

    } else {
      dispatch(setFilterTimestep('year_week'));
    }

    dispatch(setFilterDate({
      start: newStartDate.toISOString(),
      end: newEndDate.toISOString(),
    }));

    dispatch(setFilterDateCompare({
      compareStart: subDays(new Date(), 60).toISOString(),
      compareEnd: subDays(new Date(), 31).toISOString(),
    }))

    dispatch(setFilterKeyword([]));

    dispatch(setFilterDevice([]));
    dispatch(setFilterCountry([]));
    dispatch(setFilterCompare(false));
    dispatch(setFilterPlatform([]))

    setResetKeywordFilter(true);
    // Puis remettre à false après un court délai
    setTimeout(() => setResetKeywordFilter(false), 100);

    if (workspace?.template == 'E-commerce') {
      dispatch(setFilterCampaignType([]))
    }
  }

  const onChangePreset = (value: string) => {
    dateRangeSelect.onChangePreset(value);
  };

  const onChangeTimeStep = (value: string) => {
    dispatch(setFilterTimestep(value));
  }

  const iconDevices: any = [
    { name: "TABLET", icon: "icon-park:ipad" },
    { name: "MOBILE", icon: "icon-park:iphone" },
    { name: "DESKTOP", icon: "icon-park:i-mac" }
  ]

  useEffect(() => {
    if (!filterCountry || filterCountry !== filterStore.loadedCountries && (matchCompetitor || matchCompetitorMonitoring || matchCompetitorFocus || matchPLAGlobalFocus || matchTXTGlobalFocus) && filterStore.loadedCountries.length > 0) {
      setFilterCountryState(filterStore.loadedCountries);
    }

    if (!filterDevice || filterDevice !== filterStore.loadedDevices && (matchCompetitor || matchCompetitorFocus || matchCompetitorMonitoring) && filterStore.loadedDevices.length > 0) {
      setFilterDeviceState(filterStore.loadedDevices);
    }
  }, [filterStore, matchCompetitor, matchCompetitorMonitoring, matchCompetitorFocus, location, filterDevice, filterCountry, matchPLAGlobalFocus, filterStore.loadedCountries]);

  useEffect(() => {
    if (filterCountry) {
      const maxLengthCountry = filterCountry.reduce((max: any, country: any) => {
        return country.length > max ? country.length : max;
      }, 0);

      // Calculez la largeur minimale requise
      const calculatedWidth = Math.min(315, maxLengthCountry * 10); // Ajustez le multiplicateur si nécessaire

      setMinWidth(Math.max(calculatedWidth, 200)); // Appliquez une largeur minimale de 200
    }
  }, [filterCountry]);


  // Fonction pour gérer le bouton "Only" pour les pays
  const handleOnlyFilter = (option: string) => {
    dispatch(setFilterCountry([option]));
  };

  const savingsFormulaOptions = [
    { label: "Savings: Average", value: "m1" },
    { label: "Savings: Month over Month", value: "y1" }
  ];

  const savingsFormulaFilter = savingsFormulaOptions.find(
    (opt) => opt.value === filterStore.savingsFormulaFilter
  );

  const { data: keywordsDataSerpHistory } = useGetKeywordsBrandWaybackQuery({
    startDate: filterStore?.start !== undefined ? fDate(filterStore?.start, "yyyy-MM-dd") : null,
    endDate: filterStore?.end !== undefined ? fDate(filterStore?.end, "yyyy-MM-dd") : null,
    device: filterStore?.devices,
    countries: filterStore?.countries,
    workspaceId: workspace?.id,
    platforms: filterStore?.platforms
  }, {
    skip: (!filterStore?.start ||
      !filterStore?.end ||
      !workspace?.id ||
      location?.pathname === "/brand-overview"
    ),
    refetchOnMountOrArgChange: false,
  });


  useEffect(() => {
    if (keywordsDataSerpHistory && location.pathname === "/brand-wayback") {
      dispatch(setFilterKeyword([keywordsDataSerpHistory?.filters?.keywords[0]?.search_term]));
      setFilterKeywordState(keywordsDataSerpHistory?.filters?.keywords);
    }
  }, [keywordsDataSerpHistory]);

  useEffect(() => {
    if (devices && !matchCompetitor && location?.pathname !== "/brand-wayback") {
      setFilterDeviceState((prevState: any) =>
        JSON.stringify(prevState) !== JSON.stringify(devices) ? devices : prevState
      );
    }

    if (devicesDataBrandWayback?.filters?.devices && location?.pathname === "/brand-wayback") {
      const brandWaybackDevices = devicesDataBrandWayback.filters.devices;

      setFilterDeviceState((prevState: any) =>
        JSON.stringify(prevState) !== JSON.stringify(brandWaybackDevices) ? brandWaybackDevices : prevState
      );

      // Effectuer le dispatch uniquement si le filtre est vide
      if (!filterStore.devices || filterStore.devices.length === 0) {
        const desktopDevice = brandWaybackDevices.find((device: any) => device.toLowerCase() === "desktop");
        if (desktopDevice && !filterStore.devices.includes(desktopDevice)) {
          dispatch(setFilterDevice([desktopDevice]));
        }
      }
    }
  }, [devices, matchCompetitor, location?.pathname, devicesDataBrandWayback, filterStore.devices]);

  const [conversionTypeOptions, setConversionTypeOptions] = useState<string[]>([]);
  const [conversionType, setConversionType] = useState<string>('');

  useEffect(() => {
    if (!workspace) return;

    let options: string[] = [];
    let defaultOption = '';

    if (workspace.custom === true) {
      options = ["Brand (Custom source)", "Brand (Ad-based)"];
      defaultOption = "Brand (Custom source)";
    } else if (workspace.conversions_google_analytics && workspace.conversions_google_analytics.length > 0) {
      options = ["Total", "Brand (Tracking-based)", "Brand (Ad-based)"];
      defaultOption = "Total";
    } else {
      options = ["Brand (Ad-based)"];
      defaultOption = "Brand (Ad-based)";
    }

    setConversionTypeOptions(options);
    setConversionType(defaultOption);
    dispatch(setFilterConversionType(defaultOption));
  }, [workspace]);


  useEffect(() => {
    if (workspace?.name.includes('Demo')) {
      const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

      const newStartDate = new Date(2024, 8, 1); // 1er septembre 2024
      const newEndDate = new Date(2024, 10, 30); // 30 novembre 2024

      const { start, end } = getDateRangeIso(newStartDate, newEndDate, userTimeZone);

      dispatch(setFilterDate({ start, end }));
      dispatch(setFilterTimestep("date"))
    }
  }, [workspace, location?.pathname])

  const renderContent = (
    <>
      {lgUp && isNavHorizontal && <Logo sx={{ mr: 2.5 }} />}

      {!lgUp && (
        <IconButton onClick={onOpenNav}>
          <SvgColor src="/assets/icons/navbar/ic_menu_item.svg" />
        </IconButton>
      )}

      {!lgUp &&
        <FiltersButton />
      }

      {(lgUp) &&
        <>

          {(keywordsData && countriesData && devicesData && filterStore?.minDatePicker) && showFilters &&
            <Stack width={"100%"}>
              <Box>
                {(location.pathname !== "/campaign-explorer") && (
                  <Stack sx={{ height: 54, display: 'flex' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>

                      {!["/optimization/traffic-details", "/optimization/funnel", "/brand-overview", "/optimization/overview", "/brand-wayback", "/conversions-analysis"].includes(location.pathname) && (
                        <InfiniteScrollKeywords
                          keywords={(filterStore?.loadedKeyword || [])}
                          onKeywordChange={(it: any) => handleKeywordFilterChange(it)}
                          resetFilter={resetKeywordFilter}
                        />
                      )}


                      {(filterStore?.loadedCountries?.length >= 2 && !["/brand-overview", "/competitive-intelligence/competitive-landscape", "/competitive-intelligence/overview", "/competitive-intelligence/st-analysis", "/competitive-intelligence/ads"].includes(location.pathname)) &&

                        <Autocomplete
                          multiple={location.pathname === "/brand-wayback" ? false : true}
                          disableClearable={location.pathname === "/brand-wayback" ? true : false}
                          size="small"
                          limitTags={1}
                          disableCloseOnSelect
                          options={filterCountry || []}
                          getOptionLabel={(option: any) => option}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              sx={{ minWidth: 240, maxWidth: 300 }}
                            />
                          )}
                          value={
                            filterStore?.countries?.length === 0
                              ? [t('dashboard.filters.all_countries')]
                              : filterStore?.countries
                          }
                          renderOption={(props, option) => {
                            const country = countriesJson?.find((c) => c.name === option);
                            const flagIcon = country ? `circle-flags:${country.code}` : null;

                            return (
                              <li {...props} ref={(el) => (optionRefs.current[option] = el)}
                                style={{ whiteSpace: 'nowrap' }} key={option} onMouseEnter={() => {
                                  optionRefs.current[option].classList.add('hovered');
                                }}
                                onMouseLeave={() => {
                                  optionRefs.current[option].classList.remove('hovered');
                                }}>
                                {option !== t('dashboard.filters.all') && (
                                  <Box
                                    display="flex"
                                    justifyContent="space-between"
                                    alignItems="center"
                                    width="100%"
                                    sx={{
                                      '&:hover .only-button': {
                                        display: 'flex',
                                      },
                                    }}
                                  >
                                    <Box>
                                      {location.pathname !== "/brand-wayback" &&
                                        <Checkbox
                                          checked={filterStore?.countries.includes(option)}
                                        />
                                      }

                                      {flagIcon ? (
                                        <Iconify icon={flagIcon} width={16} sx={{ mr: 1, position: 'relative', top: 4 }} />
                                      ) : (
                                        <img style={{ width: 14, height: 14, marginRight: 6, position: 'relative', top: -6 }} src="/assets/location.png" alt="flag" />
                                      )}
                                      <span style={{ position: 'relative', top: 2 }}>{option}</span>
                                    </Box>
                                    {location.pathname !== "/brand-wayback" &&
                                      <Button
                                        className="hover-only-button"
                                        onClick={(event) => {
                                          event.stopPropagation();
                                          handleOnlyFilter(option);
                                        }}
                                        variant="contained"
                                        color="inherit"
                                        size="small"
                                        sx={{
                                          mt: 0.3,
                                          height: 24,
                                          minWidth: 50,
                                          padding: 0,
                                          display: 'none',
                                          justifyContent: 'flex-end',
                                        }}
                                      >
                                        Only
                                      </Button>
                                    }
                                  </Box>
                                )}
                                {option === t('dashboard.filters.all_countries') && option}
                              </li>
                            );
                          }}
                          onChange={(event, selectedOptions) => handleCountryFilterChange(selectedOptions)}
                          renderTags={(selected, getTagProps) => {
                            const visibleTags = selected.slice(0, 1); // n’en afficher qu’un
                            const hiddenCount = selected.length - visibleTags.length;

                            return (
                              <>
                                {visibleTags.map((option, index) => {
                                  const country = countriesJson?.find((c) => c.name === option);
                                  const flagCode = country ? country.code : null;

                                  return (
                                    <Chip
                                      {...getTagProps({ index })}
                                      key={option}
                                      icon={
                                        option !== t('dashboard.filters.all_countries') ? (
                                          flagCode ? (
                                            <FlagIcon
                                              key={flagCode}
                                              code={flagCode}
                                              sx={{ width: 16, height: 16, borderRadius: '50%' }}
                                            />
                                          ) : (
                                            <img style={{ width: 14, height: 14, marginRight: 1 }} src="/assets/location.png" alt="flag" />
                                          )
                                        ) : undefined
                                      }
                                      label={option}
                                      size="small"
                                      deleteIcon={option === t('dashboard.filters.all_countries') ? <></> : undefined}
                                      variant="soft"
                                    />
                                  );
                                })}

                                {hiddenCount > 0 && (
                                  <Chip
                                    label={`+${hiddenCount}`}
                                    size="small"
                                    variant="outlined"
                                  />
                                )}
                              </>
                            );
                          }}

                        />
                      }

                      {filterStore?.loadedPlatform?.length >= 2 &&
                        <Autocomplete
                          multiple={location.pathname === "/brand-wayback" ? false : true}
                          limitTags={1}
                          disableClearable={location.pathname === "/brand-wayback" ? true : false}
                          size="small"
                          options={filterStore?.loadedPlatform}
                          getOptionLabel={(option: any) => option}
                          renderInput={(params) => (<TextField {...params} />)}
                          value={filterStore?.platforms?.length === 0 ? [t('dashboard.filters.all_platforms')] : filterStore?.platforms}
                          renderOption={(props, option) => (
                            <li {...props} key={option}>
                              {option === 'Google' || option === 'Bing' ? (
                                <Iconify
                                  icon={option === 'Google' ? 'devicon:google' : 'logos:bing'}
                                  sx={{ width: 16, height: 16, mr: 1 }}
                                />
                              ) : null}
                              {option}
                            </li>
                          )}
                          onChange={(event, selectedOptions) => {
                            let updatedOptions;

                            if (Array.isArray(selectedOptions)) {
                              updatedOptions = selectedOptions.filter(
                                (option: string) => option !== t('dashboard.filters.all_platforms')
                              );
                            } else {
                              updatedOptions = selectedOptions !== t('dashboard.filters.all_platforms') ? [selectedOptions] : [];
                            }

                            dispatch(setFilterPlatform(updatedOptions));
                          }}
                          renderTags={(selected, getTagProps) =>
                            selected.map((option, index) => (
                              <Chip
                                {...getTagProps({ index })}
                                key={option}
                                label={option}
                                icon={
                                  option === 'Google' || option === 'Bing' ? (
                                    <Iconify
                                      icon={option === 'Google' ? 'devicon:google' : 'logos:bing'}
                                      sx={{ width: 16, height: 16 }}
                                    />
                                  ) : undefined
                                }
                                size="small"
                                deleteIcon={option === t('dashboard.filters.all_platforms') ? <></> : undefined}
                                variant="soft"
                              />
                            ))
                          }
                        />
                      }

                      {!["/product-listing-ads", "/text-ads", "/brand-overview"].includes(location.pathname) &&
                        <Autocomplete
                          sx={{
                            width: 180,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            "& .MuiAutocomplete-inputRoot": {
                              flexWrap: "nowrap",   // 👈 empêche le retour à la ligne
                            },
                          }}
                          multiple={location.pathname === "/brand-wayback" ? false : true}
                          size="small"
                          options={(filterDevice || []).filter((device: any) => device !== "NA")}
                          getOptionLabel={(option: any) => option}
                          renderInput={(params) => (
                            <TextField {...params} />
                          )}
                          value={
                            filterStore?.devices?.length === 0
                              ? [t('dashboard.filters.all_devices')]
                              : location.pathname === "/brand-wayback"
                                ? [filterStore?.devices[0]?.charAt(0).toUpperCase() + filterStore?.devices[0]?.slice(1).toLowerCase()]
                                : filterStore?.devices
                          }
                          renderOption={(props, option) => (
                            <li {...props} key={option}>
                              <Iconify
                                icon={iconDevices.find((device: any) => device.name === option || option.toUpperCase())?.icon}
                                sx={{ width: 16, height: 16, mr: 1 }}
                              />
                              {option.charAt(0).toUpperCase() + option.slice(1).toLowerCase()}
                            </li>
                          )}
                          onChange={(event, selectedOptions) => handleDeviceFilterChange(selectedOptions)}
                          renderTags={(value, getTagProps) => {
                            const MAX_TAGS = 1;
                            const ALL = t('dashboard.filters.all_devices');

                            // 1) On retire le placeholder du calcul
                            const clean = (value || []).filter((v) => v !== ALL);

                            // 2) Si rien de sélectionné -> on affiche juste le placeholder (pas de +N)
                            if (clean.length === 0) {
                              return (
                                <Chip
                                  key="all-devices"
                                  label={ALL}
                                  size="small"
                                  variant="soft"
                                />
                              );
                            }

                            // 3) Calcul "shown / hidden" uniquement sur les vraies valeurs
                            const shown = clean.slice(0, MAX_TAGS);
                            const hidden = clean.length - shown.length;

                            return [
                              ...shown.map((option, index) => (
                                <Chip
                                  {...getTagProps({ index })}
                                  key={option}
                                  label={option.charAt(0).toUpperCase() + option.slice(1).toLowerCase()}
                                  icon={
                                    <Iconify
                                      icon={iconDevices.find((d: any) => d.name.toLowerCase() === option.toLowerCase())?.icon}
                                      sx={{ width: 16, height: 16 }}
                                    />
                                  }
                                  size="small"
                                  variant="soft"
                                />
                              )),
                              hidden >= 1 ? ( // <--- pas de +N quand il n'y a qu’un seul élément
                                <Chip key="more" label={`+${hidden}`} size="small" variant="outlined" />
                              ) : null,
                            ];
                          }}

                        />
                      }

                      {location.pathname === "/brand-wayback" && (
                        <InfiniteScrollKeywordsBrandWayback
                          keywords={(filterKeyword || [])}
                          onKeywordChange={(it: any) => handleKeywordFilterChange(it)}
                        />
                      )}

                      {["/competitors-monitoring"].includes(location.pathname) && (workspace?.template === 'E-commerce' || workspace?.shoppingIncluded === true) &&
                        <Autocomplete
                          multiple={true}
                          limitTags={1}
                          size="small"
                          sx={{ marginRight: 1 }}
                          options={campaignType || []}
                          getOptionLabel={(option: any) => option}
                          renderInput={(params) => (
                            <TextField {...params} label={t('dashboard.filters.campaignType')} />
                          )}
                          value={filterStore?.campaignType?.length === 0 ? [t('dashboard.filters.all')] : filterStore?.campaignType}
                          renderOption={(props, option) => (
                            <li {...props} key={option}>

                              {option.charAt(0).toUpperCase() + option.slice(1).toLowerCase()}
                            </li>
                          )}
                          onChange={(event, selectedOptions) => handleCampaignTypeFilterChange(selectedOptions)}
                          renderTags={(selected, getTagProps) =>
                            selected.map((option, index) => (
                              <Chip
                                {...getTagProps({ index })}
                                key={option}
                                label={option.charAt(0).toUpperCase() + option.slice(1).toLowerCase()}
                                icon={
                                  <Iconify
                                    icon={iconDevices.find((device: any) => device.name === option)?.icon}
                                    sx={{ width: 16, height: 16 }}
                                  />
                                }
                                size="small"
                                deleteIcon={option === t('dashboard.filters.all') ? <></> : undefined}
                                variant="soft"
                              />
                            ))
                          }
                        />
                      }

                      {["/traffic-details"].includes(location.pathname) && ["brand specialist", "admin"].includes(user?.role) &&

                        <Stack direction="row" justifyContent="flex-end" alignItems="center" spacing={1} width={42}>
                          <ButtonBase onClick={() => setIsFilterDrawerOpen(true)} sx={{ width: 36, height: 36, borderRadius: 1, "&:hover": { backgroundColor: "rgba(117, 117, 117, 0.5)" } }}>
                            <Iconify icon="mdi:filter-variant" />
                          </ButtonBase>
                        </Stack>
                      }
                    </Box>
                  </Stack>
                )}
              </Box>
            </Stack>
          }

        </>
      }

      <Stack
        flexGrow={1}
        display={"flex"}
        height={52}
        direction="row"
        justifyContent="flex-end"
        spacing={{ xs: 0.5, sm: 1 }}
      >
        <Box sx={{ height: "100%" }} display={"flex"} flexDirection={"row"}>
          {filterStore.minDatePicker &&
            <>
              {!["/", "/admin/features/live-monitoring/add", "/campaign-explorer", "/admin/features/live-monitoring", "/workspaces", "/workspaces/add", "/admin/users", "/admin/users/add"].includes(location.pathname) &&
                <>
                  {(lgUp) && (
                    <DateRangeSelect
                      startDate={dateRangeSelect.start || new Date()}
                      endDate={dateRangeSelect.end || new Date()}
                      compareStartDate={undefined}
                      compareEndDate={undefined}
                      selected={
                        dateRangeSelect.selected === "custom"
                          ? `${fDate(filterStore.start)} - ${fDate(filterStore.end)}`
                          : (dateRangeSelect.preset as any).displayName
                      }
                      onChangeEndDate={(endDate: Date | null) => {
                        dateRangeSelect.onChangeEndDate(endDate);
                        updateFilterDate(filterStore?.start, endDate);
                      }}
                      onChangeStartDate={(startDate: Date | null) => {
                        dateRangeSelect.onChangeStartDate(startDate);
                        updateFilterDate(startDate, filterStore?.end);
                      }}
                      onChangeCompareStartDate={(it: any) => setCompareStartDate(it)}
                      onChangeCompareEndDate={(it: any) => setCompareEndDate(it)}
                      onChangePreset={onChangePreset}
                      onChangeTimeStep={onChangeTimeStep}
                      preset={"last_30d"}
                      features={{ time_step: true }}
                      timeStep={dateRangeSelect.step as any}
                    />
                  )}
                </>
              }
            </>
          }

          <Box sx={{ ml: 1 }}>
            <NotificationsPopover />
          </Box>

          <SettingsPanel open={isFilterDrawerOpen} onClose={() => setIsFilterDrawerOpen(false)} />

          <AccountPopover />
        </Box>
      </Stack>
    </>

  );

  // calc dynamiques
  const headerHeight =
    lgUp
      ? (isNavHorizontal
        ? HEADER.H_DESKTOP_OFFSET
        : (offsetTop ? HEADER.H_DESKTOP_OFFSET : HEADER.H_DESKTOP))
      : HEADER.H_MOBILE;

  const isMonoCountry = filterStore?.loadedCountries?.length === 1;

  const tabs = {
    "/admin/users": [
      { title: "Users" },
    ],
    "/brand-overview": [
      { title: "Brand Overview" },
    ],
    "/competitors-monitoring": [
      { title: "Competitors Monitoring" },
    ],
    "/competitor-focus": [
      { title: "Competitors Focus" },
    ],
    "/text-ads": [
      { title: "Text Ads" },
    ],
    "/product-listing-ads": [
      { title: "Product Listing Ads" },
    ],
    "/brand-wayback": [
      { title: "Brand Wayback" },
    ],
    "/optimization/campaigns": [
      { title: "Campaigns" },
    ],
    "/optimization/overview": [
      { title: "Performance", label: "Overview", path: "/optimization/overview" },
      { title: "Performance", label: "Funnel", path: "/optimization/funnel" },
      { title: "Performance", label: "Explore", path: "/optimization/traffic-details" },
    ],
    "/optimization/conversions-analysis": [
      { title: "Performance", label: "Overview", path: "/optimization/overview" },
      { title: "Performance", label: "Funnel", path: "/optimization/funnel" },
      { title: "Performance", label: "Explore", path: "/optimization/traffic-details" },
    ],
    "/optimization/funnel": [
      { title: "Performance", label: "Overview", path: "/optimization/overview" },
      { title: "Performance", label: "Funnel", path: "/optimization/funnel" },
      { title: "Performance", label: "Explore", path: "/optimization/traffic-details" },
    ],
    "/optimization/traffic-details": [
      { title: "Performance", label: "Overview", path: "/optimization/overview" },
      { title: "Performance", label: "Funnel", path: "/optimization/funnel" },
      { title: "Performance", label: "Explore", path: "/optimization/traffic-details" },
    ],

    "/optimization/activations/active-mode": [
      { title: "Activations", label: "Active Mode", path: "/optimization/activations/active-mode" },
      { title: "Activations", label: "Scraping", path: "/optimization/scraping" },
      { title: "Activations", label: "KO Bidding", path: "/optimization/activations/ko-bidding", dropdown: true },
      { title: "Activations", label: "KO Bidding Auto", path: "/optimization/activations/ko-bidding-auto" },
    ],
    "/admin/features/live-bidding/ad-group-based": [
      { title: "Activations", label: "Active Mode", path: "/optimization/activations/active-mode" },
      { title: "Activations", label: "Scraping", path: "/optimization/scraping" },
      { title: "Activations", label: "KO Bidding", path: "/optimization/activations/ko-bidding", dropdown: true },
      { title: "Activations", label: "KO Bidding Auto", path: "/optimization/activations/ko-bidding-auto" },
    ],
    "/optimization/activations/ko-bidding": [
      { title: "Activations", label: "Active Mode", path: "/optimization/activations/active-mode" },
      { title: "Activations", label: "Scraping", path: "/optimization/scraping" },
      { title: "Activations", label: "KO Bidding", path: "/optimization/activations/ko-bidding", dropdown: true },
      { title: "Activations", label: "KO Bidding Auto", path: "/optimization/activations/ko-bidding-auto" },
    ],
    "/optimization/activations/ko-bidding-auto": [
      { title: "Activations", label: "Active Mode", path: "/optimization/activations/active-mode" },
      { title: "Activations", label: "Scraping", path: "/optimization/scraping" },
      { title: "Activations", label: "KO Bidding", path: "/optimization/activations/ko-bidding", dropdown: true },
      { title: "Activations", label: "KO Bidding Auto", path: "/optimization/activations/ko-bidding-auto" },
    ],
    "/optimization/scraping": [
      { title: "Activations", label: "Active Mode", path: "/optimization/activations/active-mode" },
      { title: "Activations", label: "Scraping", path: "/optimization/scraping" },
      { title: "Activations", label: "KO Bidding", path: "/optimization/activations/ko-bidding", dropdown: true },
      { title: "Activations", label: "KO Bidding Auto", path: "/optimization/activations/ko-bidding-auto" },
    ],
    "/competitive-intelligence/overview": [
      { title: "Competitive Intelligence", label: "Overview", path: "/competitive-intelligence/overview" },
      { title: "Competitive Intelligence", label: "Search Term Analysis", path: "/competitive-intelligence/st-analysis" },
      { title: "Competitive Intelligence", label: "Competitive Landscape", path: "/competitive-intelligence/competitive-landscape" },
      { title: "Competitive Intelligence", label: "Ads", path: "/competitive-intelligence/ads" },
    ],
    "/competitive-intelligence/competitive-landscape": [
      { title: "Competitive Intelligence", label: "Overview", path: "/competitive-intelligence/overview" },
      { title: "Competitive Intelligence", label: "Search Term Analysis", path: "/competitive-intelligence/st-analysis" },
      { title: "Competitive Intelligence", label: "Competitive Landscape", path: "/competitive-intelligence/competitive-landscape" },
      { title: "Competitive Intelligence", label: "Ads", path: "/competitive-intelligence/ads" },
    ],
    "/competitive-intelligence/st-analysis": [
      { title: "Competitive Intelligence", label: "Overview", path: "/competitive-intelligence/overview" },
      { title: "Competitive Intelligence", label: "Search Term Analysis", path: "/competitive-intelligence/st-analysis" },
      { title: "Competitive Intelligence", label: "Competitive Landscape", path: "/competitive-intelligence/competitive-landscape" },
      { title: "Competitive Intelligence", label: "Ads", path: "/competitive-intelligence/ads" },
    ],
    "/competitive-intelligence/ads": [
      { title: "Competitive Intelligence", label: "Overview", path: "/competitive-intelligence/overview" },
      { title: "Competitive Intelligence", label: "Search Term Analysis", path: "/competitive-intelligence/st-analysis" },
      { title: "Competitive Intelligence", label: "Competitive Landscape", path: "/competitive-intelligence/competitive-landscape" },
      { title: "Competitive Intelligence", label: "Ads", path: "/competitive-intelligence/ads" },
    ],
    "regexp:^/workspaces/(?<id>[0-9]+)": [
      { title: "Administration", label: "Workspace", path: (params: any) => `/workspaces/${params.id}/setup` },
      { title: "Administration", label: "Users", path: (params: any) => `/workspaces/${params.id}/users` },
    ],
  } as const;

  const matchLocation = (key: string) => {
    if (key.startsWith("regexp:")) {
      return new RegExp(key.replace("regexp:", "")).test(location.pathname);
    }
    return location.pathname.startsWith(key);
  }

  const getLocationParams = (key: string) => {
    if (key.startsWith("regexp:")) {
      const regex = new RegExp(key.replace("regexp:", ""));
      const match = regex.exec(location.pathname);
      return match?.groups || {};
    }
  }


  // Trouver le bon set de tabs
  const [currentPath, currentTabs] = Object.entries(tabs)
    .sort(([a,], [b,]) => b.length - a.length)
    .find(([key, value]) => matchLocation(key)) || ["", []];


  const currentParams = getLocationParams(currentPath);

  const currentTitle =
    currentTabs.find((tab: any) => tab.path === location.pathname)?.title ||
    currentTabs[0]?.title ||
    "";

  return (
    <AppBar
      sx={{
        height: HEADER.H_MOBILE,
        zIndex: theme.zIndex.appBar + 1,

        ...bgBlur({
          color: theme.palette.background.default,
        }),
        transition: theme.transitions.create(['height'], {
          duration: theme.transitions.duration.shorter,
        }),
        ...(lgUp && {
          width: `calc(100% - ${NAV.W_VERTICAL + 1}px)`,
          height: HEADER.H_DESKTOP,
          ...(isNavHorizontal && {
            width: 1,
            bgcolor: 'background.default',
            height: HEADER.H_DESKTOP_OFFSET,
          }),
          ...(isNavMini && {
            width: `calc(100% - ${NAV.W_MINI + 1}px)`,
          }),
        }),
        backgroundColor: theme.palette.mode === "dark" ? "#1c252e!important" : "#faf8f8!important"
      }}
    >

      <Toolbar
        sx={{
          height: 1,
          px: { lg: 5 },
        }}
      >
        {!["/optimization/activations/active-mode", "/optimization/activations/ko-bidding", "/optimization/activations/ko-bidding-auto", "/optimization/campaigns"].includes(location.pathname) &&
          <>
            {renderContent}
          </>
        }
      </Toolbar>

      <Box
        sx={{
          height: 52,
          width: "100%",
          position: "relative",
          "&::after": {
            content: '""',
            position: "absolute",
            bottom: 0,
            left: -1.5,
            top: -12.5,
            width: '100%',
            backgroundColor: theme.palette.mode === "dark" ? `#161C24` : 'white',
            height: 12,
            borderTop: (t) => `1.5px dashed ${t.palette.divider}`,
            borderLeft: (t) => `1.5px dashed ${t.palette.divider}`,
            borderTopLeftRadius: 12,
          },
        }}
      />

      {currentPath &&
        <Stack
          width="100%"
          mt={.1}
          sx={{
            position: 'fixed',
            top: headerHeight,
            zIndex: theme.zIndex.appBar,

            borderTopLeftRadius: 12,
            borderBottom: (t) => `1.5px dashed ${t.palette.divider}`,
            bgcolor: theme.palette.mode === "dark" ? '#22272f' : 'white',
          }}
        >
          <Box
            px={5}
            height={52}
            display="flex"
            flexDirection="row"
            alignItems="center"
          >
            <Typography mr={4} fontSize={19} fontWeight={700}>{currentTitle}</Typography>

            {currentTabs.map((tab: any) =>
              tab.dropdown ? (
                <KoBiddingDropdown key={tab.path} router={router} location={location} />
              ) : (
                <TabButton
                  sx={{ mr: 1 }}
                  key={tab.path}
                  className={location.pathname === tab.path ? "active" : ""}
                  onClick={() => {
                    mixpanel.track('Navigation item', { path: tab.path, email: user.email });
                    router.push(typeof tab.path === "function" ? tab.path(currentParams) : tab.path)
                  }}
                >
                  {tab.label}
                </TabButton>
              )
            )}
          </Box>

          {(![
            "/brand-overview",
            "/admin/features/live-bidding/ad-group-based",
            "/brand-wayback",
            "/admin/users",
            "/admin/users/add",
            "/competitors-monitoring",
            "/text-ads",
            "/product-listing-ads",
            "/competitor-focus",
            "/optimization/campaigns",
            "/optimization/scraping",
            "/optimization/activations/ko-bidding/target-impression-share",
            "/optimization/activations/ko-bidding/tcpa",
            "/optimization/activations/ko-bidding/troas",
            "/optimization/activations/ko-bidding/max-cpc",
            "/optimization/activations/active-mode",
            "/optimization/activations/ko-bidding-auto",
            "/competitive-intelligence/overview",
            "/competitive-intelligence/competitive-landscape",
            "/competitive-intelligence/st-analysis"
          ].includes(location.pathname)
            && !window.location.pathname.startsWith("/workspaces/")) && (

              <Stack
                sx={{
                  height: 52,
                  backgroundColor: theme.palette.mode === "dark" ? "#2f363f" : 'white',
                  px: 5,
                  borderTop: "1px dashed rgba(145, 158, 171, 0.2)",
                  // borderBottom: "1px solid rgba(145, 158, 171, 0.2)",
                  display: "flex",
                }}
              >
                <Box
                  height={"100%"}
                  display="flex"
                  flexDirection="row"
                  alignItems="center"
                >

                  {location.pathname === "/competitive-intelligence/ads" && (
                    <CompetitiveIntelligenceAdsHeader />
                  )}

                  {(!["/competitive-intelligence/ads", "/brand-overview", "/optimization/overview", "/brand-wayback", "/conversions-analysis"].includes(location.pathname)) && (
                    <InfiniteScrollKeywords
                      keywords={(filterStore?.loadedKeyword || [])}
                      onKeywordChange={(it: any) => handleKeywordFilterChange(it)}
                      resetFilter={resetKeywordFilter}
                    />
                  )}

                {/* {["/optimization/traffic-details"].includes(location.pathname) && ["brand specialist", "admin"].includes(user?.role) &&
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "flex-end",
                      alignItems: "center",
                      width: "100%",
                    }}
                  >
                    <ButtonBase onClick={() => setIsFilterDrawerOpen(true)} sx={{ width: 36, height: 36, borderRadius: 1, "&:hover": { backgroundColor: "rgba(117, 117, 117, 0.5)" } }}>
                      <Iconify icon="mdi:filter-variant" />
                    </ButtonBase>
                  </Box>
                } */}
                  {["/optimization/overview"].includes(location.pathname) && (

                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "flex-end",
                        alignItems: "center",
                        width: "100%",
                      }}
                    >
                      <Autocomplete
                        options={savingsFormulaOptions}
                        value={savingsFormulaFilter}
                        disableClearable
                        onChange={(_, newValue) => {
                          dispatch(setSavingsFormulaFilter(newValue?.value || "m1"));
                        }}
                        sx={{ width: 200 }}
                        renderInput={(params) => (
                          <TextField {...params} size="small" />
                        )}
                      />
                    </Box>
                  )}
                </Box>
              </Stack>
            )}
        </Stack>
      }
    </AppBar>
  );
}
